/**
 * Helpers for the booking hold / slot-reservation countdown.
 * Hold identity + countdown are persisted in sessionStorage for current tab session only.
 * Data is cleared when the tab is closed or page is refreshed.
 */

import { deleteBooking } from '../APICalling/APICalling';
import {
  getSessionStorageValue,
  removeSessionStorageValue,
  STORAGE_KEYS,
} from './SessionStorage';

// Shared by expiry, navigation and exit flows, including stale React callbacks.
// Keep successful releases until the UUID is used for another booking hold.
const holdReleases = new Map();

export const resetBookingHoldRelease = (uuid) => {
  holdReleases.delete(uuid);
};

export const releaseBookingHoldOnce = (uuid) => {
  if (holdReleases.has(uuid)) return holdReleases.get(uuid);

  const release = deleteBooking(uuid).then(
    (response) => {
      if (!response?.data?.success) holdReleases.delete(uuid);
      return response;
    },
    (error) => {
      holdReleases.delete(uuid);
      throw error;
    }
  );
  holdReleases.set(uuid, release);
  return release;
};

export const getCountdownRemainingSeconds = () => {
  const savedEndTime = sessionStorage.getItem(STORAGE_KEYS.COUNTDOWN_END_TIME);
  if (!savedEndTime) return 0;
  const endTime = parseInt(savedEndTime, 10);
  if (Number.isNaN(endTime)) return 0;
  return Math.max(0, Math.floor((endTime - Date.now()) / 1000));
};

export const hasActiveBookingHold = () => {
  const bookingId = getSessionStorageValue(STORAGE_KEYS.BOOKING_ID);
  return Boolean(bookingId) && getCountdownRemainingSeconds() > 0;
};

export const clearHoldSessionKeys = ({ expired = false } = {}) => {
  removeSessionStorageValue(STORAGE_KEYS.COUNTDOWN_END_TIME);
  removeSessionStorageValue(STORAGE_KEYS.TIMER_ACTIVE);
  removeSessionStorageValue(STORAGE_KEYS.TIMER_EXTEND);
  removeSessionStorageValue(STORAGE_KEYS.TIMES_UP);
  removeSessionStorageValue(STORAGE_KEYS.BOOKING_ID);
  if (expired) removeSessionStorageValue(STORAGE_KEYS.BOOKING_UUID);
};
